import {
    ChangeEvent,
    InputHTMLAttributes,
    MutableRefObject,
    ReactNode,
    forwardRef,
    useId,
    useState,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./RadioButton.module.scss";

export interface RadioButtonProps
    extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
    /** Visible label rendered to the right of the radio icon. */
    label?: ReactNode;
    /** Toggles label rendering. Defaults to `true`. */
    showLabel?: boolean;
    /** Error visual state (red border when unchecked). */
    error?: boolean;
    /** Convenience callback with checked boolean. */
    onCheckedChange?: (checked: boolean) => void;
}

/**
 * Treez DS — RadioButton (Wave 1).
 *
 * Source: Figma `Radio button` (`qgFv3OQga6RQ4luvSFuGFP`, node `197:36117`).
 *
 * Visual specs:
 * - icon: 20x20
 * - outer radius: full (circle)
 * - stroke: 1.5px
 * - selected fill: green05
 * - center dot: 6x6, primary black
 * - label: 15/24 regular
 */
export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
    function RadioButton(
        {
            label = "Radiobutton Label",
            showLabel = true,
            checked,
            defaultChecked,
            disabled = false,
            error = false,
            className,
            id,
            name,
            onChange,
            onCheckedChange,
            ...rest
        },
        ref,
    ) {
        const generatedId = useId();
        const inputId = id ?? `ds-radio-${generatedId}`;

        const isControlled = checked !== undefined;
        const [uncontrolledChecked, setUncontrolledChecked] = useState(
            Boolean(defaultChecked),
        );
        const currentChecked = isControlled
            ? Boolean(checked)
            : uncontrolledChecked;

        const setRefs = (node: HTMLInputElement | null) => {
            if (typeof ref === "function") {
                ref(node);
            } else if (ref) {
                (ref as MutableRefObject<HTMLInputElement | null>).current =
                    node;
            }
        };

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) {
                setUncontrolledChecked(event.target.checked);
            }
            onChange?.(event);
            onCheckedChange?.(event.target.checked);
        };

        return (
            <label
                className={cx(styles.root, className)}
                data-checked={currentChecked || undefined}
                data-disabled={disabled || undefined}
                data-error={error || undefined}
                data-ui="radio-button"
                htmlFor={inputId}
            >
                <input
                    checked={isControlled ? checked : undefined}
                    className={styles.input}
                    defaultChecked={isControlled ? undefined : defaultChecked}
                    disabled={disabled}
                    id={inputId}
                    name={name}
                    onChange={handleChange}
                    ref={setRefs}
                    type="radio"
                    {...rest}
                />

                <span aria-hidden className={styles.icon}>
                    <span className={styles.dot} />
                </span>

                {showLabel && <span className={styles.label}>{label}</span>}
            </label>
        );
    },
);

RadioButton.displayName = "RadioButton";
